import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";

const FONT = "'Cactus Classical Serif', serif";

function sanitizeHtml(html) {
  return String(html || "")
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "");
}

function RichText({ children, sx }) {
  return (
    <Typography
      component="div"
      sx={{
        fontFamily: FONT,
        whiteSpace: "pre-line",
        lineHeight: 1.75,
        color: "var(--game-text-color, #F3E8D1)",
        "& p": { margin: "0 0 0.8em" },
        ...sx,
      }}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(children) }}
    />
  );
}

const actionButtonSx = {
  minHeight: 44,
  borderRadius: "18px",
  px: 2,
  color: "var(--game-text-color, #F3E8D1)",
  border: "1px solid color-mix(in srgb, var(--game-accent-color, #C29A5B) 42%, transparent)",
  background: "rgba(15, 12, 9, 0.72)",
  fontFamily: FONT,
  letterSpacing: "0.08em",
  "&:hover": { background: "rgba(38, 29, 19, 0.9)" },
};

function TopNavigation({ mode, setMode }) {
  if (mode !== "main") {
    return (
      <Button startIcon={<ArrowBackIcon />} onClick={() => setMode("main")} sx={actionButtonSx}>
        Zpět
      </Button>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 1.2, flexWrap: "wrap", justifyContent: "center" }}>
      <Button startIcon={<ContactsOutlinedIcon />} onClick={() => setMode("contacts")} sx={actionButtonSx}>
        Kontaktovat
      </Button>
      <Button startIcon={<Inventory2OutlinedIcon />} onClick={() => setMode("archive")} sx={actionButtonSx}>
        Archiv
      </Button>
    </Box>
  );
}

function ContactsView({ contacts }) {
  const [selectedId, setSelectedId] = useState(null);
  return (
    <Box>
      <Typography sx={{ fontFamily: FONT, fontSize: 22, textAlign: "center", mb: 2, textTransform: "uppercase" }}>
        Kontakty
      </Typography>
      {contacts.length === 0 ? (
        <Typography sx={{ opacity: 0.65, textAlign: "center", fontFamily: FONT }}>
          Pro tuto část příběhu zatím nejsou dostupné žádné kontakty.
        </Typography>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(105px, 1fr))", gap: 2 }}>
          {contacts.map((contact) => {
            const selected = selectedId === contact.id;
            return (
              <Box
                component="button"
                type="button"
                key={contact.id}
                onClick={() => setSelectedId(contact.id)}
                sx={{
                  appearance: "none",
                  border: 0,
                  p: 1,
                  color: "inherit",
                  background: "transparent",
                  cursor: "pointer",
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: 88,
                    height: 88,
                    mx: "auto",
                    borderRadius: "50%",
                    overflow: "hidden",
                    border: selected
                      ? "2px solid var(--game-accent-color, #C29A5B)"
                      : "1px solid rgba(243,232,209,0.25)",
                    boxShadow: selected ? "0 0 24px rgba(194,154,91,0.28)" : "none",
                    backgroundColor: "rgba(255,255,255,0.05)",
                  }}
                >
                  {contact.imageUrl ? (
                    <Box component="img" src={contact.imageUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <ContactsOutlinedIcon sx={{ fontSize: 42, mt: 2.5, opacity: 0.5 }} />
                  )}
                </Box>
                <Typography sx={{ mt: 1, fontFamily: FONT, fontSize: 14 }}>{contact.name}</Typography>
                {!!contact.role && <Typography sx={{ fontSize: 11, opacity: 0.58 }}>{contact.role}</Typography>}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

function ArchiveView({ items }) {
  const [openId, setOpenId] = useState(null);
  return (
    <Box>
      <Typography sx={{ fontFamily: FONT, fontSize: 22, textAlign: "center", mb: 2, textTransform: "uppercase" }}>
        Archiv
      </Typography>
      {items.length === 0 ? (
        <Typography sx={{ opacity: 0.65, textAlign: "center", fontFamily: FONT }}>
          Archiv je zatím prázdný.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
          {items.map((item) => {
            const isOpen = openId === item.id;
            return (
              <Box
                key={item.id}
                onClick={() => setOpenId(isOpen ? null : item.id)}
                sx={{
                  border: "1px solid rgba(243,232,209,0.16)",
                  background: "rgba(16,13,10,0.72)",
                  p: 1.5,
                  cursor: "pointer",
                }}
              >
                <Typography sx={{ fontFamily: FONT, letterSpacing: "0.05em" }}>{item.title}</Typography>
                {isOpen && (
                  <Box sx={{ pt: 1.2 }}>
                    {item.imageUrl && (
                      <Box component="img" src={item.imageUrl} alt="" sx={{ width: "100%", maxHeight: 210, objectFit: "contain", mb: 1.2 }} />
                    )}
                    <RichText sx={{ fontSize: 13 }}>{item.content}</RichText>
                    {item.audioUrl && <audio controls src={item.audioUrl} style={{ width: "100%", marginTop: 10 }} />}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

function ObjectiveCard({ objective, value, onChange, onSubmit, submitting, feedback }) {
  return (
    <Box
      sx={{
        position: "relative",
        p: 2,
        borderRadius: "16px",
        border: objective.solved
          ? "1px solid color-mix(in srgb, var(--game-accent-color, #C29A5B) 70%, white)"
          : "1px solid rgba(243,232,209,0.15)",
        background: objective.solved ? "rgba(57,48,30,0.7)" : "rgba(8,7,6,0.66)",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "flex-start" }}>
        <Box>
          <Typography sx={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", opacity: 0.62 }}>
            {objective.type === "main" ? "Main objective" : "Side objective"}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 18, mt: 0.5 }}>{objective.title}</Typography>
        </Box>
        {objective.solved && <CheckCircleOutlineIcon sx={{ color: "var(--game-accent-color, #C29A5B)" }} />}
      </Box>

      {!!objective.prompt && <RichText sx={{ fontSize: 13, mt: 1 }}>{objective.prompt}</RichText>}

      {!objective.solved && (
        <Box sx={{ display: "flex", gap: 1, mt: 1.5, alignItems: "stretch" }}>
          <TextField
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder="Zadejte odpověď"
            fullWidth
            size="small"
            onKeyDown={(event) => {
              if (event.key === "Enter") onSubmit();
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 44,
                color: "var(--game-text-color, #F3E8D1)",
                fontFamily: FONT,
                background: "rgba(0,0,0,0.35)",
                "& fieldset": { borderColor: "rgba(243,232,209,0.18)" },
                "&:hover fieldset": { borderColor: "rgba(243,232,209,0.35)" },
                "&.Mui-focused fieldset": { borderColor: "var(--game-accent-color, #C29A5B)" },
              },
              input: { textAlign: "center" },
            }}
          />
          <IconButton disabled={submitting || !value.trim()} onClick={onSubmit} sx={{ ...actionButtonSx, width: 44, borderRadius: "12px" }}>
            {submitting ? <CircularProgress size={19} color="inherit" /> : <SendOutlinedIcon fontSize="small" />}
          </IconButton>
        </Box>
      )}

      {!!feedback && (
        <Typography sx={{ mt: 1, fontSize: 12, color: feedback.correct ? "#b8d8a7" : "#df9d91" }}>
          {feedback.correct ? "Objective splněn." : "To není správná odpověď."}
        </Typography>
      )}
    </Box>
  );
}

export default function EscapeBoxGameScreen({ backendUrl, code, userId, onQuit }) {
  const [session, setSession] = useState(null);
  const [mode, setMode] = useState("main");
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState({});
  const [loading, setLoading] = useState(true);
  const [submittingId, setSubmittingId] = useState(null);
  const [continuing, setContinuing] = useState(false);
  const [error, setError] = useState("");

  const loadSession = useCallback(async () => {
    if (!code) return;
    setLoading(true);
    setError("");
    try {
      const query = userId ? `?userId=${encodeURIComponent(userId)}` : "";
      const response = await fetch(`${backendUrl}/api/escape-box/session/${encodeURIComponent(code)}${query}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Escape-box hru se nepodařilo načíst.");
      setSession(data);
      setMode("main");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, code, userId]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const submitObjective = async (objective) => {
    const answer = answers[objective.id] || "";
    if (!answer.trim() || submittingId) return;
    setSubmittingId(objective.id);
    try {
      const response = await fetch(`${backendUrl}/api/escape-box/objectives/${objective.id}/answer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, userId, answer }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Odpověď se nepodařilo ověřit.");
      setFeedback((previous) => ({ ...previous, [objective.id]: { correct: data.correct } }));
      setSession((previous) => ({
        ...previous,
        screen: {
          ...previous.screen,
          objectives: data.objectives,
          canContinue: data.canContinue,
        },
      }));
    } catch (requestError) {
      setFeedback((previous) => ({ ...previous, [objective.id]: { correct: false } }));
      setError(requestError.message);
    } finally {
      setSubmittingId(null);
    }
  };

  const continueStory = async () => {
    if (continuing) return;
    setContinuing(true);
    setError("");
    try {
      const response = await fetch(`${backendUrl}/api/escape-box/continue`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, userId }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Ve hře se nepodařilo pokračovat.");
      setAnswers({});
      setFeedback({});
      await loadSession();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setContinuing(false);
    }
  };

  if (loading) {
    return <Box sx={{ minHeight: "70dvh", display: "grid", placeItems: "center" }}><CircularProgress color="inherit" /></Box>;
  }

  if (error && !session) {
    return (
      <Box sx={{ minHeight: "70dvh", display: "grid", placeItems: "center", p: 3, textAlign: "center" }}>
        <Box><Typography sx={{ fontFamily: FONT, mb: 2 }}>{error}</Typography><Button onClick={loadSession} sx={actionButtonSx}>Zkusit znovu</Button></Box>
      </Box>
    );
  }

  if (session?.completed) {
    return (
      <Box sx={{ minHeight: "85dvh", p: 3, display: "flex", flexDirection: "column", justifyContent: "center", textAlign: "center" }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 28, textTransform: "uppercase" }}>{session.gameName}</Typography>
        <RichText sx={{ mt: 3 }}>{session.message}</RichText>
        <Button onClick={onQuit} sx={{ ...actionButtonSx, mt: 4, alignSelf: "center", minWidth: 180 }}>Zpět</Button>
      </Box>
    );
  }

  const screen = session?.screen;
  if (!screen) return null;

  if (screen.kind === "story") {
    return (
      <Box sx={{ minHeight: "90dvh", p: { xs: 2, sm: 3 }, display: "flex", flexDirection: "column" }}>
        <Typography sx={{ fontSize: 11, opacity: 0.5, letterSpacing: "0.2em", textAlign: "center", textTransform: "uppercase" }}>{session.gameName}</Typography>
        {screen.imageUrl && <Box component="img" src={screen.imageUrl} alt="" sx={{ width: "100%", maxHeight: "38dvh", objectFit: "contain", mt: 2 }} />}
        {!!screen.headline && <Typography sx={{ fontFamily: FONT, fontSize: 23, textAlign: "center", mt: 2 }}>{screen.headline}</Typography>}
        {screen.audioUrl && <audio controls src={screen.audioUrl} style={{ width: "100%", marginTop: 18 }} />}
        <RichText sx={{ mt: 2, textAlign: "center", fontSize: 14 }}>{screen.text}</RichText>
        {!!error && <Typography sx={{ mt: 1, color: "#df9d91", textAlign: "center" }}>{error}</Typography>}
        <Button disabled={continuing} onClick={continueStory} sx={{ ...actionButtonSx, mt: "auto", mx: "auto", width: "min(420px, 92%)" }}>
          {continuing ? <CircularProgress size={20} color="inherit" /> : screen.buttonText}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100dvh", display: "flex", flexDirection: "column", color: "var(--game-text-color, #F3E8D1)" }}>
      <Box
        sx={{
          minHeight: "45dvh",
          p: { xs: 2, sm: 3 },
          borderBottom: "1px solid rgba(243,232,209,0.13)",
          backgroundColor: "var(--game-surface-color, #211A12)",
          backgroundImage: "linear-gradient(180deg, rgba(255,255,255,0.025), rgba(0,0,0,0.2)), var(--game-screen-texture, none)",
          backgroundRepeat: "repeat",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, mb: 3 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, letterSpacing: "0.12em", textTransform: "uppercase", opacity: 0.72 }}>
            {session.gameName}
          </Typography>
          <TopNavigation mode={mode} setMode={setMode} />
        </Box>

        {mode === "contacts" ? (
          <ContactsView contacts={screen.contacts || []} />
        ) : mode === "archive" ? (
          <ArchiveView items={screen.archive || []} />
        ) : (
          <Box sx={{ textAlign: "center", maxWidth: 560, mx: "auto" }}>
            {screen.imageUrl && <Box component="img" src={screen.imageUrl} alt="" sx={{ width: "100%", maxHeight: 220, objectFit: "contain", mb: 2 }} />}
            {!!screen.headline && <Typography sx={{ fontFamily: FONT, fontSize: 25, textTransform: "uppercase" }}>{screen.headline}</Typography>}
            {!!screen.text && <RichText sx={{ mt: 1.5, fontSize: 14 }}>{screen.text}</RichText>}
            {screen.audioUrl && <audio controls src={screen.audioUrl} style={{ width: "100%", marginTop: 16 }} />}
          </Box>
        )}
      </Box>

      <Box sx={{
        p: { xs: 2, sm: 3 },
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.24)",
        backgroundImage: "linear-gradient(rgba(0,0,0,0.16), rgba(0,0,0,0.16)), var(--game-panel-texture, none)",
        backgroundRepeat: "repeat",
      }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 22, textAlign: "center", textTransform: "uppercase", mb: 2 }}>
          Objectives
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxWidth: 600, mx: "auto" }}>
          {(screen.objectives || []).map((objective) => (
            <ObjectiveCard
              key={objective.id}
              objective={objective}
              value={answers[objective.id] || ""}
              onChange={(value) => setAnswers((previous) => ({ ...previous, [objective.id]: value }))}
              onSubmit={() => submitObjective(objective)}
              submitting={submittingId === objective.id}
              feedback={feedback[objective.id]}
            />
          ))}
          {(screen.objectives || []).length === 0 && (
            <Typography sx={{ textAlign: "center", opacity: 0.6, fontFamily: FONT }}>Pro tuto obrazovku nejsou definované žádné objectives.</Typography>
          )}
          {!!error && <Typography sx={{ color: "#df9d91", textAlign: "center", fontSize: 13 }}>{error}</Typography>}
          {screen.canContinue && (
            <Button disabled={continuing} onClick={continueStory} sx={{ ...actionButtonSx, mt: 1, minHeight: 52 }}>
              {continuing ? <CircularProgress size={20} color="inherit" /> : "Pokračovat"}
            </Button>
          )}
          <Button onClick={onQuit} sx={{ color: "inherit", opacity: 0.55, fontFamily: FONT, mt: 1 }}>Opustit hru</Button>
        </Box>
      </Box>
    </Box>
  );
}
